import RequestBodyNormalize from '../api/RequestBodyNormalize';
import {
  API_QUOTE_CALCULATION_PATHS,
  API_QUOTE_SAVE_PATHS,
  ORDER_TYPES,
  PRICEBOOK_TAGS,
  SCHEDULE_TYPE_OPTIONS,
} from '../common/enums';
import isVariablesEqual from '../common/helpers/isVariablesEqual';
import { PartnerContext, PriceBookContext } from '../Context';
import { CONTACT_PROPS } from '../components/CustomerContactInfo/ContactSectionProps';
import { BILLING_EMAIL_VALIDATION_STATUSES } from '../common/enums/billingEmailValidationStatuses';

const VALIDATION_TRIGGERS_ON = {
  ALWAYS: 1,
  API_SAVE_SCHEMA_CHANGE: 2,
  API_CALC_SCHEMA_CHANGE: 3,
};

// const VALIDATION_RESULT_TYPE = {
//   get isGroup() {
//     return this.groupLabel.length;
//   },
//   groupLabel: '',
//   messages: []
// };

class ValidateLocationQuote {
  constructor(locationQuoteModelInstance) {
    this._locBean = null;
    this._locationQuoteModelInstance = locationQuoteModelInstance;

    /**
     * @type {CustomerOrderModel}
     * @private
     */
    this._moBean = null;
    /**
     * @private
     */
    this._apiSaveRequestBody = {
      prev: null,
      curr: null,
    };
    /**
     * @private
     */
    this._apiCalculationRequestBody = {
      prev: null,
      curr: null,
    };

    this.results = new Map();
  }

  /**
   * @returns {CustomerOrderModel}
   */
  get moBean() {
    return this._moBean;
  }

  /**
   * @returns {LocationQuoteModel|null}
   */
  get locBean() {
    if (this._locBean !== null) {
      return this._locBean;
    }

    this._locBean = this.fetchLocBean(this.moBean);
    return this._locBean;
  }

  fetchLocBean(moBean) {
    if (this._locBean !== null) {
      return this._locBean;
    }

    for (let i = 0; i < moBean.locations.length; i++) {
      if (moBean.locations[i].locationUuid === this._locationQuoteModelInstance.locationUuid) {
        return moBean.locations[i];
      }
    }

    return null;
  }

  _validate(validateFunction, triggersOn = VALIDATION_TRIGGERS_ON.ALWAYS) {
    let isBeanChanged = false;

    switch (triggersOn) {
      case VALIDATION_TRIGGERS_ON.API_SAVE_SCHEMA_CHANGE:
        isBeanChanged = !isVariablesEqual(this._apiSaveRequestBody.curr, this._apiSaveRequestBody.prev);
        break;

      case VALIDATION_TRIGGERS_ON.API_CALC_SCHEMA_CHANGE:
        isBeanChanged = !isVariablesEqual(this._apiCalculationRequestBody.curr, this._apiCalculationRequestBody.prev);
        break;

      case VALIDATION_TRIGGERS_ON.ALWAYS:
      default:
        isBeanChanged = true;
    }

    if (!isBeanChanged) {
      return null;
    }

    const result = validateFunction(this.moBean, this.locBean);

    this.results.set(validateFunction.name, result);

    // this.results.delete(validateFunction.name);

    return this.results.get(validateFunction.name);
  }

  /**
   * @param {CustomerOrderModel} moBean
   */
  setMasterOrderBean(moBean) {
    if (typeof moBean !== 'object' && moBean === null) {
      throw new Error('Invalid data object');
    }

    this._locBean = null;
    this._moBean = moBean;

    this._apiSaveRequestBody.prev = this._apiSaveRequestBody.curr;
    this._apiSaveRequestBody.curr = this.fetchLocBean(
      RequestBodyNormalize(API_QUOTE_SAVE_PATHS[moBean.orderType], moBean)
    );

    delete this._apiSaveRequestBody.curr.inputVersion;

    this._apiCalculationRequestBody.prev = this._apiCalculationRequestBody.curr;
    this._apiCalculationRequestBody.curr = this.fetchLocBean(
      RequestBodyNormalize(API_QUOTE_CALCULATION_PATHS[moBean.orderType], moBean)
    );
  }

  isValidForSignOrFinalize() {
    return this._validate(ValidateLocationQuote.isValidForSignOrFinalize, VALIDATION_TRIGGERS_ON.API_SAVE_SCHEMA_CHANGE);
  }

  static isValidForSignOrFinalize(moBean, locBean) {
    const REQUIRED_FOR_FINALIZE_LOC = {
      serviceInfo: {
        streetAddress1: '',
        city: '',
        stateId: null,
        countryId: null,
        zip: '',
        btn: '',
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactOfficePhone: '',
      },
      billingInfo: {
        billToId: null,
        contactFirstName: '',
        contactLastName: '',
        streetAddress1: '',
        city: '',
        stateId: '',
        zip: '',
        countryId: '',
        contactOfficePhone: '',
        contactEmail: '',
      },
      shippingInfo: {
        companyName: '',
        contactFirstName: '',
        contactLastName: '',
        streetAddress1: '',
        city: '',
        stateId: null,
        countryId: null,
        zip: '',
      },
      dateShippingRequested: false,
      trackingLocationId: false,
    };
    const WLW_EXCLUDED_FIELDS = ['contactOfficePhone', 'contactEmail'];
    const VALID_EMAIL_STATUSES = [
      BILLING_EMAIL_VALIDATION_STATUSES.VALID,
      BILLING_EMAIL_VALIDATION_STATUSES.CONFIRMED_AS_VALID,
    ];

    let missingFields = [];

    for (let el in REQUIRED_FOR_FINALIZE_LOC) {
      if (typeof REQUIRED_FOR_FINALIZE_LOC[el] === 'object') {
        const requiredFields = Object.keys(REQUIRED_FOR_FINALIZE_LOC[el]);
        let missing = requiredFields.filter(subEl => !locBean[el][subEl]);

        if (el === 'serviceInfo' && PartnerContext.model.isWhiteLabelWholesale) {
          missing = missing.filter(field => !WLW_EXCLUDED_FIELDS.includes(field));
        }

        if (el === 'billingInfo' && PriceBookContext.model.isSupportsCommUnity) {
          missing = missing.filter(field => field !== 'billToId');
        }

        if (missing.length && el === 'serviceInfo') {
          if (missing.length === 1) {
            missingFields.push({
              fieldName: el,
              fieldLabel:
                '@msg_service_information @ @ ' + CONTACT_PROPS[el][missing].label + ' @ @ msg_field_remaining@',
            });
          } else {
            missingFields.push({
              fieldName: el,
              fieldLabel: '@msg_service_information @ ' + missing.length + ' @ msg_fields_remaining@',
            });
          }
        } else if (missing.length && el === 'billingInfo') {
          if (missing.length === 1) {
            missingFields.push({
              fieldName: el,
              fieldLabel:
                '@msg_billing_information @ @ ' + CONTACT_PROPS[el][missing].label + ' @ @ msg_field_remaining@',
            });
          } else {
            missingFields.push({
              fieldName: el,
              fieldLabel: '@msg_billing_information @ ' + missing.length + ' @ msg_fields_remaining@',
            });
          }
        } else if (missing.length && el === 'shippingInfo') {
          if (missing.length === 1) {
            missingFields.push({
              fieldName: el,
              fieldLabel:
                '@msg_shipping_information @ @ ' + CONTACT_PROPS[el][missing].label + ' @ @ msg_field_remaining@',
            });
          } else {
            missingFields.push({
              fieldName: el,
              fieldLabel: '@msg_shipping_information @ ' + missing.length + ' @ msg_fields_remaining@',
            });
          }
        }
      } else if (!PriceBookContext.model.isSupportsCommUnity && !locBean[el] && el === 'dateShippingRequested') {
        missingFields.push({
          fieldName: el,
          fieldLabel: '@msg_ictp_ship_install_dates@',
        });
      } else if (!PriceBookContext.model.isSupportsCommUnity && !locBean[el] && el === 'trackingLocationId') {
        const fieldLabel =
          moBean.orderType === ORDER_TYPES.REWRITE
            ? '@msg_tracking_location_id_rewrites@'
            : '@msg_tracking_location_id@';

        missingFields.push({
          fieldName: el,
          fieldLabel,
        });
      }
    }

    if (!PriceBookContext.model.isSupportsCommUnity && moBean.trackingLocationId === null) {
      missingFields.push({
        fieldName: 'trackingLocationId',
        fieldLabel: '@msg_finalize_master_order@',
      });
    }

    if (locBean.calcResults.getValue('sdWanPrerequisite4gWarning', 'boolean')) {
      missingFields.push({
        fieldName: '4gWarning',
        fieldLabel: '@msg_star_cloud_locations_ordering@',
      });
    }

    if (
      PriceBookContext.model.flags.pricebookTag.includes(PRICEBOOK_TAGS.SUPPORTS_NEW_DAAS) &&
      moBean.daasScheduleType === SCHEDULE_TYPE_OPTIONS.MIXED &&
      locBean.calcResults.getValue('numScheduledDaaS', 'integer') > 0 &&
      locBean.daasScheduleType === SCHEDULE_TYPE_OPTIONS.NONE
    ) {
      missingFields.push({
        fieldName: 'daasScheduleTypeOption',
        fieldLabel: '@msg_select_daas_schedule_type@',
      });
    }

    const fieldsMessage = missingFields.length ? missingFields.map(m => m.fieldLabel).join('@\n') : '';
    let message = missingFields.length !== 0 ? `@msg_enter_required_fields@:\n @${fieldsMessage}@\n` : '';

    const skipSendgridValidation = PartnerContext.model.isWhiteLabelWholesale;

    if (!skipSendgridValidation) {
      if (!VALID_EMAIL_STATUSES.includes(locBean.billingInfo.contactEmailValidationStatus)) {
        message += '@msg_validate_email_address_message@\n';
        missingFields.push({});
      }
    }

    // TODO: Investigate and fix how come missingFields array has elements with empty objects
    missingFields = missingFields.filter(field => Object.getOwnPropertyNames(field).length);

    return {
      message: message,
      fields: missingFields,
    };
  }
}

export default ValidateLocationQuote;
